import cx from "classnames";
import React, { type CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { formatSecondsToMMSS } from "@/appInterface/windows/StartVoteWindow/helper";
import style from "./CurrentVoteInfo.module.css";

type CurrentVoteInfoProps = {
    onClose: () => void;
    vote: {
        duration: number;
        text: string;
        oneVotePerItem: boolean;
        start: string;
        availableVotesCount: number;
    };
    isCreator: boolean;
    addTimerDuration: (duration: number) => void;
    finishVote: () => void;
};

const MAX_DURATION = 99 * 60;

export function CurrentVoteInfo({
    onClose,
    vote,
    isCreator,
    addTimerDuration,
    finishVote,
}: CurrentVoteInfoProps) {
    const [timeLeft, setTimeLeft] = useState(0);
    const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        const diffInMs: number = new Date().getTime() - new Date(vote.start ?? 0).getTime();
        const diffInSeconds: number = (vote.duration ?? 0) - Math.floor(diffInMs / 1000);

        if (diffInSeconds <= 0) return;
        setTimeLeft(diffInSeconds);

        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        intervalRef.current = setInterval(() => {
            setTimeLeft((prev) => {
                if (prev <= 1) {
                    if (intervalRef.current) {
                        clearInterval(intervalRef.current);
                        intervalRef.current = null;
                    }
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [vote.duration, vote.start]);

    const handleAddTime = useCallback(
        (seconds: number) => {
            const addedSeconds = Math.min(seconds, MAX_DURATION - timeLeft);
            if (addedSeconds > 0) {
                addTimerDuration(addedSeconds);
            }
        },
        [addTimerDuration, timeLeft],
    );

    return (
        <div className={`${style.voteInfoWindow} ${isCreator ? style.isCreator : ""} `}>
            <div className={style.header}>
                <h2 className={style.title}>{vote.text}</h2>
                <button
                    onClick={onClose}
                    className={cx(style.close, style.button, "button--close")}
                    type="button"
                    aria-label="Закрыть"
                />
                <div className={style.tags}>
                    <div className={style.tag}>Анонимно</div>
                    <div className={style.tag}>
                        {vote.oneVotePerItem
                            ? "Один голос на объект"
                            : "Несколько голосов на объект"}
                    </div>
                </div>
            </div>
            <div className={style.details}>
                <div className={style.inputs}>
                    <div className={`${style.box} ${isCreator ? style.boxEditable : ""}`}>
                        <div className={style.boxInner}>
                            <div className={style.boxTitle}>Время</div>
                            <div className={style.boxDescription}>осталось</div>
                            <div
                                className={`${style.boxValue} ${timeLeft <= 30 ? style.red : ""}`}
                                data-value=""
                            >
                                {formatSecondsToMMSS(timeLeft)}
                            </div>
                        </div>

                        <div className={style.boxTimeButtons}>
                            <button
                                type="button"
                                className={cx(
                                    style.button,
                                    "button",
                                    "button--small",
                                    "button--outline",
                                )}
                                onClick={() => handleAddTime(60)}
                            >
                                + 1
                            </button>
                            <button
                                type="button"
                                className={cx(
                                    style.button,
                                    "button",
                                    "button--small",
                                    "button--outline",
                                )}
                                onClick={() => handleAddTime(180)}
                            >
                                + 3
                            </button>
                        </div>
                    </div>
                    <div className={style.box}>
                        <div className={style.boxInner}>
                            <div className={style.boxTitle}>Голоса</div>
                            <div className={style.boxDescription}>осталось</div>
                            <div className={style.boxValue} data-value="">
                                {vote.availableVotesCount}
                            </div>
                        </div>
                    </div>
                </div>

                <div className={style.progress}>
                    <div className={style.progressTitle}>
                        <span className={style.progressTitleText}>Проголосовали:</span>
                        <span className={style.progressCounter}>3/5</span>
                    </div>
                    <div
                        className={style.progressBar}
                        style={{ "--width": "66%" } as CSSProperties}
                    />
                </div>
            </div>
            {isCreator && (
                <div className={style.buttons}>
                    <button
                        type="button"
                        className={cx(style.button, "button", "button--wide")}
                        onClick={finishVote}
                    >
                        Завершить для всех
                    </button>
                </div>
            )}
        </div>
    );
}
